import { useEffect, useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import InputField from '../../form/input/InputField';
import Select from '../../form/Select';
import MultiSelectCheckbox from '../../form/MultiSelectCheckbox';
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import Alert from "../../ui/alert/Alert";
import { useNavigate } from "react-router";
import axios from "axios";
import { authService } from "../../../services/authService";
import { Copy } from "lucide-react";

interface AstronMembersFormData {
    active: boolean;
    name: string;
    productId: string;
    offersList: string[];
    url: string;
    token: string;
}

interface NewAstronMembersFormProps {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    companyId?: string;
}

export default function NewAstronMembersForm({ onSuccess, onError, companyId: companyIdProp }: NewAstronMembersFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<AstronMembersFormData>({
        name: "",
        productId: "",
        offersList: [],
        url: "",
        active: true,
        token: "",
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
    const [copiedToken, setCopiedToken] = useState(false);
    const [copiedProductId, setCopiedProductId] = useState(false);

    const companyId = companyIdProp || authService.getCompanyId?.();

    // Gera um token curto de 11 caracteres
    const generateShortToken = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let token = '';
        for (let i = 0; i < 11; i++) {
            token += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return token;
    };

    // Gera o token quando o componente monta
    useEffect(() => {
        const token = generateShortToken();
        setFormData((prev) => ({ ...prev, token }));
    }, []);

    // Busca os produtos da empresa
    useEffect(() => {
        const fetchProducts = async () => {
            setLoadingProducts(true);
            setProducts([]);
            try {
                const resp = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/products`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );
                if (resp.data && resp.data.products && Array.isArray(resp.data.products)) {
                    const mappedProducts = resp.data.products.map((product: any) => ({
                        value: product.id,
                        label: product.name
                    }));
                    setProducts(mappedProducts);
                }
            } catch (error) {
                setProducts([]);
                setApiError("Erro ao buscar produtos.");
            } finally {
                setLoadingProducts(false);
            }
        };

        fetchProducts();
    }, []);

    // Busca as ofertas de um produto específico
    const fetchProductOffers = async (productId: string) => {
        setLoadingOffers(true);
        setOffers([]);
        setSelectedOffers([]);
        setSelectAllOffers(false);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.offers && Array.isArray(resp.data.offers)) {
                const mappedOffers = resp.data.offers.map((offer: any) => ({
                    value: offer.id,
                    label: offer.name
                }));
                setOffers(mappedOffers);
            }
        } catch (error) {
            setOffers([]);
            setApiError("Erro ao buscar ofertas do produto.");
        } finally {
            setLoadingOffers(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setErrors((prev) => ({ ...prev, [id]: "" }));
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [id]: checked }));
    };

    const handleProductChange = async (value: string) => {
        setFormData((prev) => ({ ...prev, productId: value, offersList: [] }));
        setErrors((prev) => ({ ...prev, productId: "", offersList: "" }));
        await fetchProductOffers(value);
    };

    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');
        setErrors((prev) => ({ ...prev, offersList: "" }));
        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormData((prev) => ({
                ...prev,
                offersList: [], // Array vazio = qualquer oferta
            }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormData((prev) => ({
                ...prev,
                offersList: values, // Ofertas específicas selecionadas
            }));
        }
    };

    const handleCopyToken = async () => {
        try {
            await navigator.clipboard.writeText(formData.token);
            setCopiedToken(true);
            setTimeout(() => setCopiedToken(false), 2000);
        } catch (err) {
            console.error('Erro ao copiar token:', err);
        }
    };

    const handleCopyProductId = async () => {
        try {
            await navigator.clipboard.writeText(formData.productId);
            setCopiedProductId(true);
            setTimeout(() => setCopiedProductId(false), 2000);
        } catch (err) {
            console.error('Erro ao copiar ID do produto:', err);
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.name) newErrors.name = "Nome da integração é obrigatório";
        if (!formData.productId) newErrors.productId = "Selecione um produto";
        if (!selectAllOffers && !formData.offersList.length)
            newErrors.offersList = "Selecione ao menos uma oferta ou 'Qualquer oferta'";
        if (!formData.url) newErrors.url = "URL é obrigatória";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setApiError("");
        setApiSuccess("");
        try {
            const integrationData: any = {
                name: formData.name,
                provider: "astronmembers",
                apiUrl: "https://api.astronmembers.com.br",
                companyId,
                productId: formData.productId,
                active: formData.active,
                token: formData.token,
                webhookUrl: formData.url,
                offersList: formData.offersList,
            };

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/membership`,
                integrationData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );

            if (response.data && response.data.success) {
                setApiSuccess("Integração criada com sucesso!");
                if (onSuccess) onSuccess(response.data);
                setTimeout(() => navigate('/apps/astronmembers'), 1200);
            } else {
                setApiError(response.data?.error || "Erro ao criar integração");
                if (onError) onError(response.data?.error || "Erro desconhecido");
            }
        } catch (error: any) {
            setApiError(error.response?.data?.error || error.message || "Erro ao criar integração");
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => navigate(`/apps/astronmembers`);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Nova integração AstronMembers">
                    {/* Mensagens de erro e sucesso */}
                    {apiError && (
                        <div className="mt-4">
                            <Alert
                                variant="error"
                                title="Erro"
                                message={apiError}
                            />
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="mt-4">
                            <Alert
                                variant="success"
                                title="Sucesso"
                                message={apiSuccess}
                            />
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar integração"
                                checked={formData.active}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome da integração *</Label>
                            <InputField
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        {/* Token gerado automaticamente */}
                        <div className="mt-5">
                            <Label htmlFor="token">Token de integração</Label>
                            <div className="relative">
                                <InputField
                                    type="text"
                                    id="token"
                                    value={formData.token}
                                    disabled={true}
                                />
                                <button
                                    type="button"
                                    onClick={handleCopyToken}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                    title="Copiar token"
                                >
                                    <Copy className={`h-4 w-4 ${copiedToken ? 'text-green-500' : 'text-gray-500'}`} />
                                </button>
                            </div>
                            {copiedToken && (
                                <p className="mt-1 text-xs text-green-500">Token copiado!</p>
                            )}
                        </div>

                        <div className="mt-5">
                            <Label>Produto *</Label>
                            <Select
                                options={products}
                                placeholder="Selecione um produto"
                                onChange={handleProductChange}
                                value={formData.productId}
                                className={errors.productId ? "border-red-500" : ""}
                            />
                            {errors.productId && <p className="mt-1 text-sm text-red-500">{errors.productId}</p>}
                            {loadingProducts && (
                                <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                    Carregando produtos...
                                </div>
                            )}
                        </div>

                        {/* Ofertas do produto */}
                        {formData.productId && (
                            <div className="mt-5">
                                <Label>Ofertas *</Label>
                                <MultiSelectCheckbox
                                    options={[
                                        { id: 'all', label: 'Qualquer oferta' },
                                        ...offers.map(offer => ({ id: offer.value, label: offer.label }))
                                    ]}
                                    selectedValues={selectedOffers}
                                    onChange={handleOffersChange}
                                    placeholder="Selecione as ofertas"
                                    showSelectAll={false}
                                    className={errors.offersList ? "border-red-500" : ""}
                                />
                                {errors.offersList && <p className="mt-1 text-sm text-red-500">{errors.offersList}</p>}
                                {loadingOffers && (
                                    <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                        Carregando ofertas...
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ID do produto selecionado */}
                        {formData.productId && (
                            <div className="mt-5">
                                <Label htmlFor="productId">ID do Produto</Label>
                                <div className="relative">
                                    <InputField
                                        type="text"
                                        id="productIdDisplay"
                                        value={formData.productId}
                                        disabled={true}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleCopyProductId}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                        title="Copiar ID do produto"
                                    >
                                        <Copy className={`h-4 w-4 ${copiedProductId ? 'text-green-500' : 'text-gray-500'}`} />
                                    </button>
                                </div>
                                {copiedProductId && (
                                    <p className="mt-1 text-xs text-green-500">ID do produto copiado!</p>
                                )}
                            </div>
                        )}

                        <div className="mt-5">
                            <Label htmlFor="url">URL AstronMembers *</Label>
                            <InputField
                                type="text"
                                id="url"
                                placeholder="Cole aqui a URL fornecida pela AstronMembers"
                                value={formData.url}
                                onChange={handleInputChange}
                                error={!!errors.url}
                                hint={errors.url}
                            />
                        </div>

                        <div className="mt-6 flex justify-end gap-4.5">
                            <Button
                                size="md"
                                variant="outline"
                                onClick={handleGoBack}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="md"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : "Salvar integração"}
                            </Button>
                        </div>
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}
